import type { RepairEvent } from '@avisoma/shared';
import { SourcePageCell } from './SourcePageCell';
import { Td, Th, TruncatedTd } from './cells';
import { FindingMarkerCell, FindingMarkerHeader, type RowFindings } from './FindingMarkerCell';
import { formatRepairApproval, formatRepairLevel, formatRepairPart } from './repairPresentation';
import {
  extractionRowClass,
  extractionTableClass,
  extractionTbodyClass,
  extractionTheadClass,
} from './tableClasses';

/**
 * Dense grid for consolidated repair / workshop report events (DR-4817).
 *
 * Like the borescope table a row carries `pageIds` (plural): consolidation groups the pages of
 * one repaired unit into a single event, so the source column lists each contributing page. The
 * date `…Raw` / `…Label` evidence lanes are read from the row inspector.
 */
export function RepairTable({
  rows,
  pageIdToPosition,
  onInspect,
  rowFindings,
}: {
  rows: RepairEvent[];
  pageIdToPosition?: Map<string, number>;
  onInspect: (row: RepairEvent, index: number) => void;
  rowFindings?: RowFindings;
}) {
  return (
    <table className={extractionTableClass}>
      <thead className={extractionTheadClass}>
        <tr>
          {rowFindings && <FindingMarkerHeader />}
          {pageIdToPosition && <Th>Pages</Th>}
          <Th>Subject</Th>
          <Th>Part</Th>
          <Th>Description</Th>
          <Th>Ex-engine ESN</Th>
          <Th>Work Order</Th>
          <Th>Received</Th>
          <Th>Completed</Th>
          <Th>Removed</Th>
          <Th>Reason</Th>
          <Th>Level</Th>
          <Th>Work Performed</Th>
          <Th>Repair Station</Th>
          <Th>Approval</Th>
        </tr>
      </thead>
      <tbody className={extractionTbodyClass}>
        {rows.map((row, index) => {
          // `pageIds` is a jsonb-backed array — a row written mid-reprocessing can reach the UI
          // without it, so never map over it unguarded.
          const pageIds = row.pageIds ?? [];
          return (
            <tr
              key={`${row.partNumber ?? row.workOrder ?? 'repair'}-${pageIds[0] ?? 'page'}-${index}`}
              onClick={() => onInspect(row, index)}
              className={extractionRowClass}
            >
              {rowFindings && <FindingMarkerCell findings={rowFindings.get(index)} />}
              {pageIdToPosition && (
                <Td>
                  {pageIds.length > 0 ? (
                    <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                      {pageIds.map((pageId) => (
                        <SourcePageCell key={pageId} pageId={pageId} pageIdToPosition={pageIdToPosition} />
                      ))}
                    </div>
                  ) : (
                    '—'
                  )}
                </Td>
              )}
              <TruncatedTd value={row.subject} />
              <TruncatedTd value={formatRepairPart(row)} className="font-mono" />
              <TruncatedTd value={row.description} />
              <Td className="font-mono">{row.esn ?? '—'}</Td>
              <Td className="font-mono">{row.workOrder ?? '—'}</Td>
              <Td>{row.dateReceived ?? '—'}</Td>
              <Td>{row.dateCompleted ?? '—'}</Td>
              <Td>{row.removalDate ?? '—'}</Td>
              <TruncatedTd value={row.removalReason} />
              <TruncatedTd value={formatRepairLevel(row)} />
              <TruncatedTd value={row.workPerformed} maxWidth="max-w-[360px]" />
              <TruncatedTd value={row.repairStation} />
              <TruncatedTd value={formatRepairApproval(row)} />
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
